import { useState } from "react";
import { ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls = "w-full min-w-0 bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";

export default function KycForm({ kyc, phone, onChange }) {
  const [form, setForm] = useState({ full_name: kyc?.full_name || "", bank_name: kyc?.bank_name || "", account_number: "", ifsc: kyc?.ifsc || "", upi_id: kyc?.upi_id || "", phone: phone || "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const status = kyc?.status || "none";

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Upload a photo of your Aadhaar / PAN / Voter ID");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("id_doc", file);
      await api.post("/customer/kyc", fd);
      toast.success("KYC submitted — we'll verify within 24 hours");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit KYC");
    } finally {
      setBusy(false);
    }
  };

  const badge = { none: ["Not submitted", "bg-ink/5 text-ink/60"], pending: ["Under review", "bg-amber-50 text-amber-700"], approved: ["Verified", "bg-emerald-50 text-emerald-700"], rejected: ["Rejected — resubmit", "bg-red-50 text-red-700"] }[status];

  return (
    <div className="bg-white border border-ink/10 rounded-3xl p-6" data-testid="kyc-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-xl font-bold flex items-center gap-2"><ShieldCheck size={18} className="text-terra" /> Bank & KYC</h3>
        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${badge[1]}`} data-testid="kyc-status">{badge[0]}</span>
      </div>
      {status === "rejected" && kyc?.note && <p className="mt-2 text-xs text-red-700" data-testid="kyc-note">{kyc.note}</p>}
      {status === "approved" ? (
        <p className="mt-3 text-sm text-ink/60" data-testid="kyc-approved-text">Payouts go to {kyc.bank_name} ····{String(kyc.account_number || "").slice(-4)} ({kyc.ifsc}){kyc.upi_id ? ` or UPI ${kyc.upi_id}` : ""}.</p>
      ) : status === "pending" ? (
        <p className="mt-3 text-sm text-ink/60" data-testid="kyc-pending-text">Submitted on {new Date(kyc.submitted_at).toLocaleDateString("en-IN")}. We'll notify you on WhatsApp once verified.</p>
      ) : (
        <form onSubmit={submit} className="mt-4 grid grid-cols-1 gap-3" data-testid="kyc-form">
          <p className="text-xs text-ink/60">Required once before your first withdrawal. Details are used only for bank transfer.</p>
          <input required placeholder="Full name (as on bank account)" value={form.full_name} onChange={set("full_name")} className={inputCls} data-testid="kyc-full-name" />
          <input required placeholder="Bank name" value={form.bank_name} onChange={set("bank_name")} className={inputCls} data-testid="kyc-bank-name" />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Account number" value={form.account_number} onChange={set("account_number")} className={inputCls} data-testid="kyc-account-number" />
            <input required placeholder="IFSC" value={form.ifsc} onChange={set("ifsc")} className={`${inputCls} uppercase`} data-testid="kyc-ifsc" />
          </div>
          <input placeholder="UPI ID (optional)" value={form.upi_id} onChange={set("upi_id")} className={inputCls} data-testid="kyc-upi" />
          <input required type="tel" placeholder="WhatsApp number (for payout updates)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="kyc-phone" />
          <label className="flex items-center gap-3 border border-dashed border-ink/20 rounded-xl px-4 py-3 text-sm cursor-pointer hover:bg-sand transition-colors">
            <Upload size={16} className="text-terra shrink-0" />
            <span className="truncate">{file ? file.name : "Upload ID proof (Aadhaar / PAN / Voter ID photo)"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="kyc-id-doc" />
          </label>
          <button type="submit" disabled={busy} data-testid="kyc-submit" className="bg-ink text-sand text-sm font-semibold py-3 rounded-full hover:bg-terra transition-colors disabled:opacity-50">{busy ? "Submitting…" : "Submit KYC"}</button>
        </form>
      )}
    </div>
  );
}
